import React from 'react';
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  Image,
  Info,
  MapPin,
  Tag,
  User,
} from 'lucide-react';

import css from './BookingPageIcons.module.css';

/**
 * Lucide icons for the listing booking flow — matches NOWOffers_WebsiteCode booking.$id.tsx.
 * Sizes: section/badge h-4 (16px), breadcrumb/location h-3 (12px), confirmation check h-8 (32px).
 */
const lucideBase = {
  fill: 'none',
  'aria-hidden': true,
};

/** Section title badge — reference: className="h-4 w-4" */
export const bookingSectionIconProps = {
  ...lucideBase,
  size: 16,
  strokeWidth: 2,
};

/** Breadcrumb chevron & summary MapPin — reference: className="h-3 w-3" */
export const bookingSmallIconProps = {
  ...lucideBase,
  size: 12,
  strokeWidth: 2,
};

/** Apply on icon or parent — prevents sanitize/global CSS from filling Lucide paths */
export const bookingLucideClassName = css.lucideStroke;

const wrapIcon = (Icon, defaultProps) => {
  const Wrapped = ({ className, size, strokeWidth, ...rest }) => (
    <Icon
      className={[bookingLucideClassName, className].filter(Boolean).join(' ')}
      size={size ?? defaultProps.size}
      strokeWidth={strokeWidth ?? defaultProps.strokeWidth}
      {...lucideBase}
      {...rest}
    />
  );
  Wrapped.displayName = Icon.displayName || Icon.name;
  return Wrapped;
};

export const BookingCalendarIcon = wrapIcon(Calendar, bookingSectionIconProps);
export const BookingClockIcon = wrapIcon(Clock, bookingSectionIconProps);
export const BookingUserIcon = wrapIcon(User, bookingSectionIconProps);
export const BookingImageIcon = wrapIcon(Image, bookingSectionIconProps);
export const BookingInfoIcon = wrapIcon(Info, bookingSectionIconProps);
export const BookingTagIcon = wrapIcon(Tag, bookingSectionIconProps);
export const BookingChevronRightIcon = wrapIcon(ChevronRight, bookingSmallIconProps);
export const BookingChevronLeftIcon = wrapIcon(ChevronLeft, bookingSmallIconProps);
export const BookingMapPinIcon = wrapIcon(MapPin, bookingSmallIconProps);
export const BookingCreditCardIcon = wrapIcon(CreditCard, bookingSectionIconProps);

/** Gallery prev/next — slightly larger than breadcrumb chevrons */
export const bookingGalleryArrowProps = {
  ...lucideBase,
  size: 18,
  strokeWidth: 2,
};

/** Staff row selected — reference: Check className="h-4 w-4 ..." */
export const BookingStaffCheckIcon = wrapIcon(Check, bookingSectionIconProps);

/** Confirmation hero — reference: Check className="h-8 w-8" strokeWidth={3} */
export const BookingConfirmedCheckIcon = ({ className, ...rest }) => (
  <Check
    className={[bookingLucideClassName, className].filter(Boolean).join(' ')}
    size={32}
    strokeWidth={3}
    {...lucideBase}
    {...rest}
  />
);
